import { Card, CardContent, CardHeader, CardTitle } from '@lms/ui/card';
import { Checkbox } from '@lms/ui/checkbox';
import { Input } from '@lms/ui/input';
import { Label } from '@lms/ui/label';
import { type FormEvent, useState } from 'react';
import { useSWRConfig } from 'swr';
import { useTranslations } from 'use-intl';
import { SubmitButton } from '@/components/submit-button';
import { useBrand } from '@/features/branding/brand-provider';
import { addUser, usersKey } from './users-api';

// Ports app/[brand]/admin/users/_components/add-user.tsx. The Next app used a
// form-action hook bound to a server action; the SPA calls `addUser` directly,
// tracks pending/message/error locally, and revalidates the list via SWR.
export default function AddUser() {
  const t = useTranslations();
  const { brand: site } = useBrand();
  const { mutate } = useSWRConfig();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [canManageGroups, setCanManageGroups] = useState(false);
  const [canManageUsers, setCanManageUsers] = useState(false);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPending(true);
    setMessage('');
    setError(undefined);

    try {
      await addUser({
        name,
        email,
        canManageGroups,
        canManageUsers,
        siteId: site.id,
      });
      await mutate(usersKey(site.id));
      setMessage(t('admin.added', { what: name }));
      setName('');
      setEmail('');
      setCanManageGroups(false);
      setCanManageUsers(false);
    } catch (err) {
      setError(`${err}`);
    } finally {
      setPending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('admin.add', {
            what: t('admin.user').toLowerCase(),
          })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form className="m-auto mt-8 max-w-md" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-4">
            <Input
              type="text"
              name="name"
              placeholder={t('admin.name')}
              className="input input-bordered flex-grow"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Input
              type="text"
              name="email"
              placeholder={t('admin.email')}
              className="input input-bordered flex-grow"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="canManageGroups"
                  checked={canManageGroups}
                  onCheckedChange={(checked) =>
                    setCanManageGroups(checked === true)
                  }
                />
                <Label htmlFor="canManageGroups">
                  {t('permissions.manageGroups')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="canManageUsers"
                  checked={canManageUsers}
                  onCheckedChange={(checked) =>
                    setCanManageUsers(checked === true)
                  }
                />
                <Label htmlFor="canManageUsers">
                  {t('permissions.manageUsers')}
                </Label>
              </div>
            </div>
            {error && <div className="w-full text-red-500">{error}</div>}
            {message && <div className="w-full text-green-500">{message}</div>}
            <SubmitButton
              pending={pending}
              label={t('admin.add', {
                what: t('admin.user').toLowerCase(),
              })}
            />
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
